import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useKandidaatMutaties } from '../../api/kandidaten.ts';
import { zoekLocaties } from '../../api/referentie.ts';
import type { Dienstverband, Functietitel, Locatie, WensenDetails } from '../../api/typen.ts';
import { ZoekMeerkeuzeveld } from '../../componenten/AsyncKeuzeveld.tsx';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { FunctietitelsVeld, locatieLabel } from '../../componenten/keuzevelden.tsx';
import { Meerkeuzeveld, Tekstveld } from '../../componenten/velden.tsx';
import { leegNaarNull, naarGetal, naarTekst } from '../../helpers/formulier.ts';
import { dienstverbandLabels, opties } from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface Waarden {
  urenMin: string;
  urenMax: string;
  salarisMin: string;
  salarisMax: string;
  dienstverbanden: Dienstverband[];
  maxReisafstandKm: string;
  zoektVanaf: string;
  functies: Functietitel[];
  locaties: Locatie[];
}

interface Props {
  open: boolean;
  onSluit: () => void;
  kandidaatId: string;
  bestaand: WensenDetails | null;
}

const dienstverbandOpties = opties(dienstverbandLabels);

function beginwaarden(bestaand: WensenDetails | null): Waarden {
  return {
    urenMin: naarTekst(bestaand?.urenMin),
    urenMax: naarTekst(bestaand?.urenMax),
    salarisMin: naarTekst(bestaand?.salarisMin),
    salarisMax: naarTekst(bestaand?.salarisMax),
    dienstverbanden: bestaand?.dienstverbanden ?? [],
    maxReisafstandKm: naarTekst(bestaand?.maxReisafstandKm),
    zoektVanaf: bestaand?.zoektVanaf ?? '',
    functies: bestaand?.functietitels.map((koppeling) => koppeling.functietitel) ?? [],
    locaties: bestaand?.locaties.map((koppeling) => koppeling.locatie) ?? [],
  };
}

/**
 * Legt de wensen van een kandidaat vast: uren, salaris, dienstverband, gewenste functies en werklocaties.
 * @param props De kandidaat en zijn huidige wensen.
 * @returns De dialoog.
 */
export function WensenFormulier({ open, onSluit, kandidaatId, bestaand }: Props) {
  const mutaties = useKandidaatMutaties();
  const formulier = useForm<Waarden>({ defaultValues: beginwaarden(bestaand) });
  useEffect(() => {
    if (open) {
      formulier.reset(beginwaarden(bestaand));
    }
  }, [open, bestaand, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) =>
      mutaties.slaWensenOp.mutateAsync({
        id: kandidaatId,
        invoer: {
          urenMin: naarGetal(waarden.urenMin),
          urenMax: naarGetal(waarden.urenMax),
          salarisMin: naarGetal(waarden.salarisMin),
          salarisMax: naarGetal(waarden.salarisMax),
          dienstverbanden: waarden.dienstverbanden,
          maxReisafstandKm: naarGetal(waarden.maxReisafstandKm),
          zoektVanaf: leegNaarNull(waarden.zoektVanaf),
          functies: waarden.functies.map((functietitel) => ({ functietitelId: functietitel.id })),
          locatieIds: waarden.locaties.map((locatie) => locatie.id),
        },
      }),
    { melding: 'Wensen opgeslagen', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Wensen"
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="md"
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Tekstveld naam="urenMin" label="Uren minimaal" type="number" min={1} max={168} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Tekstveld naam="urenMax" label="Uren maximaal" type="number" min={1} max={168} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Tekstveld naam="salarisMin" label="Salaris minimaal" type="number" stap="0.01" min={0} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Tekstveld naam="salarisMax" label="Salaris maximaal" type="number" stap="0.01" min={0} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Meerkeuzeveld naam="dienstverbanden" label="Dienstverbanden" opties={dienstverbandOpties} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Tekstveld naam="maxReisafstandKm" label="Max. reisafstand (km)" type="number" min={1} max={1000} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Tekstveld naam="zoektVanaf" label="Zoekt vanaf" type="date" />
          </Grid>
          <Grid size={12}>
            <FunctietitelsVeld naam="functies" label="Gewenste functies" />
          </Grid>
          <Grid size={12}>
            <ZoekMeerkeuzeveld<Locatie>
              naam="locaties"
              label="Gewenste werklocaties"
              sleutel="locaties"
              laad={zoekLocaties}
              optieLabel={locatieLabel}
              hulptekst="Zoek op plaats of postcode"
            />
          </Grid>
        </Grid>
      </FormulierDialoog>
    </FormProvider>
  );
}
